import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FaUpload, FaTrash, FaEdit, FaSave, FaTimes, FaImage, FaSearchPlus, FaCloudUploadAlt, FaCheckCircle } from 'react-icons/fa';
import { getPhotos, uploadPhoto, updatePhotoComment, deletePhoto } from '../api/client';
import ImageLightbox from './ImageLightbox';
import ConfirmDialog from './ConfirmDialog';
import { useToast, ToastContainer } from './Toast';

const PAID_TIERS = ['individual', 'group', 'starter_agent', 'professional_agent', 'enterprise_agency', 'promotional', 'unlimited', 'admin'];
const FREE_PHOTO_LIMIT = 3;

const PhotoGallery = ({ requestId, isOwner, isMember = false, userTier, limitsWaived = false, onViewPricing }) => {
  // Both owners and active members can upload photos
  const canUpload = isOwner || isMember;
  // limitsWaived: non-paid user on a short trip (<= 3 days) gets full access.
  const isFreeUser = !limitsWaived && !PAID_TIERS.includes(userTier || 'free');
  const [photos, setPhotos] = useState([]);
  const [photoUrls, setPhotoUrls] = useState({});
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]); // Multiple files
  const [filePreviews, setFilePreviews] = useState([]); // Preview URLs
  const [uploadProgress, setUploadProgress] = useState({}); // Track upload status per file
  const [editingPhotoId, setEditingPhotoId] = useState(null);
  const [editComment, setEditComment] = useState('');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [failedPhotos, setFailedPhotos] = useState(new Set());

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, photoId: null });

  // Toast notifications
  const { toasts, removeToast, showSuccess, showError, showWarning } = useToast();

  // Ref to track if component is mounted
  const isMountedRef = useRef(true);
  const dropZoneRef = useRef(null);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchPhotos = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getPhotos(requestId);

      // Only update state if component is still mounted
      if (!isMountedRef.current) return;

      setPhotos(data.photos || []);

      // Fetch image data for each photo
      const urls = {};
      const failed = new Set();
      for (const photo of data.photos || []) {
        if (!isMountedRef.current) return;
        try {
          const blob = await fetchPhotoBlob(photo.id);
          if (!isMountedRef.current) return;
          urls[photo.id] = URL.createObjectURL(blob);
        } catch (error) {
          console.error(`Error fetching photo ${photo.id}:`, error);
          failed.add(photo.id);
        }
      }
      if (isMountedRef.current) {
        setPhotoUrls(urls);
        setFailedPhotos(failed);
      }
    } catch (error) {
      if (!isMountedRef.current) return;
      console.error('Error fetching photos:', error);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [requestId]);

  useEffect(() => {
    fetchPhotos();
    // Cleanup blob URLs when component unmounts
    return () => {
      setPhotoUrls(prevUrls => {
        Object.values(prevUrls).forEach(url => URL.revokeObjectURL(url));
        return {};
      });
    };
  }, [fetchPhotos]);

  const fetchPhotoBlob = async (photoId) => {
    const runtimeConfig = window.__RUNTIME_CONFIG__ || {};
    const API_BASE_URL = runtimeConfig.API_URL || process.env.REACT_APP_API_URL || `${window.location.origin}/api`;
    const token = localStorage.getItem('access_token');

    const response = await fetch(`${API_BASE_URL}/photos/${photoId}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch photo');
    }

    return await response.blob();
  };

  // Validate a single file
  const validateFile = (file) => {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      return { valid: false, error: `${file.name}: Invalid file type` };
    }
    if (file.size > 10 * 1024 * 1024) {
      return { valid: false, error: `${file.name}: File too large (max 10MB)` };
    }
    return { valid: true };
  };

  // Process multiple files
  const processFiles = (files) => {
    const fileArray = Array.from(files);
    const validFiles = [];
    const errors = [];

    fileArray.forEach(file => {
      const validation = validateFile(file);
      if (validation.valid) {
        validFiles.push(file);
      } else {
        errors.push(validation.error);
      }
    });

    if (errors.length > 0) {
      showWarning(`Some files were skipped: ${errors.join(', ')}`);
    }

    if (validFiles.length > 0) {
      // Create previews
      const previews = validFiles.map(file => ({
        file,
        url: URL.createObjectURL(file),
        name: file.name
      }));

      setSelectedFiles(prev => [...prev, ...validFiles]);
      setFilePreviews(prev => [...prev, ...previews]);
    }
  };

  const handleFileSelect = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      processFiles(files);
    }
    // Reset input to allow selecting same files again
    e.target.value = '';
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set dragging false if we're leaving the drop zone entirely
    if (e.currentTarget === dropZoneRef.current && !e.currentTarget.contains(e.relatedTarget)) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      processFiles(files);
    }
  };

  // Remove a file from selection
  const removeSelectedFile = (index) => {
    // Revoke the preview URL to free memory
    URL.revokeObjectURL(filePreviews[index].url);

    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setFilePreviews(prev => prev.filter((_, i) => i !== index));
  };

  // Clear all selected files
  const clearAllFiles = () => {
    filePreviews.forEach(preview => URL.revokeObjectURL(preview.url));
    setSelectedFiles([]);
    setFilePreviews([]);
    setUploadProgress({});
  };

  const hasReachedPhotoLimit = isFreeUser && photos.length >= FREE_PHOTO_LIMIT;

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      showWarning('Please select files first');
      return;
    }

    if (isFreeUser) {
      const remaining = FREE_PHOTO_LIMIT - photos.length;
      if (remaining <= 0) {
        showWarning('Free plan photo limit reached. Upgrade for unlimited photos.');
        if (onViewPricing) onViewPricing();
        return;
      }
      if (selectedFiles.length > remaining) {
        showWarning(`Free plan allows ${FREE_PHOTO_LIMIT} photos. You can upload ${remaining} more.`);
        return;
      }
    }

    try {
      setUploading(true);
      const totalFiles = selectedFiles.length;
      let successCount = 0;
      let failCount = 0;

      // Upload files one by one
      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        setUploadProgress(prev => ({ ...prev, [i]: 'uploading' }));

        try {
          await uploadPhoto(requestId, file, null);
          setUploadProgress(prev => ({ ...prev, [i]: 'success' }));
          successCount++;
        } catch (error) {
          console.error(`Error uploading ${file.name}:`, error);
          setUploadProgress(prev => ({ ...prev, [i]: 'error' }));
          failCount++;
          // If limit reached, redirect to pricing
          const detail = error.response?.data?.detail || '';
          if (detail.includes('Upgrade') || detail.includes('Free plan')) {
            showWarning(detail);
            setTimeout(() => { if (onViewPricing) onViewPricing(); }, 1500);
            break;
          }
        }
      }

      // Show result
      if (failCount > 0 && !isFreeUser) {
        showWarning(`Uploaded ${successCount} of ${totalFiles} photos. ${failCount} failed.`);
      } else if (successCount > 0) {
        showSuccess(`Successfully uploaded ${successCount} photo${successCount > 1 ? 's' : ''}`);
      }

      // Clear after a short delay to show completion status
      setTimeout(() => {
        clearAllFiles();
        fetchPhotos();
      }, 1000);

    } catch (error) {
      console.error('Error uploading photos:', error);
      showError('Failed to upload photos. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDeletePhoto = (photoId) => {
    setConfirmDialog({ isOpen: true, photoId });
  };

  const confirmDeletePhoto = async () => {
    const photoId = confirmDialog.photoId;
    setConfirmDialog({ isOpen: false, photoId: null });

    try {
      await deletePhoto(requestId, photoId);
      showSuccess('Photo deleted successfully');
      await fetchPhotos();
    } catch (error) {
      console.error('Error deleting photo:', error);
      showError('Failed to delete photo. Please try again.');
    }
  };

  const handleEditComment = (photo) => {
    setEditingPhotoId(photo.id);
    setEditComment(photo.comment || '');
  };

  const handleSaveComment = async (photoId) => {
    try {
      await updatePhotoComment(requestId, photoId, editComment);
      setEditingPhotoId(null);
      setEditComment('');
      showSuccess('Comment updated');
      await fetchPhotos();
    } catch (error) {
      console.error('Error updating comment:', error);
      showError('Failed to update comment. Please try again.');
    }
  };

  const handleCancelEdit = () => {
    setEditingPhotoId(null);
    setEditComment('');
  };

  const handleImageClick = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  // Prepare images array for lightbox
  const lightboxImages = photos
    .filter(photo => photoUrls[photo.id])
    .map(photo => ({
      url: photoUrls[photo.id],
      comment: photo.comment,
      alt: photo.filename
    }));

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex flex-col items-center gap-3">
          <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-neutral-600 font-medium">Loading photos...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <ToastContainer toasts={toasts} removeToast={removeToast} />
      <div className="bg-white rounded-xl shadow-medium p-6 space-y-6">
        {/* Header */}
        <h3 className="text-2xl font-bold text-neutral-800 flex items-center gap-3 pb-3 border-b border-neutral-300">
          <div className="w-10 h-10 bg-gradient-secondary rounded-full flex items-center justify-center">
            <FaImage className="text-white text-xl" />
          </div>
          Trip Photos
        </h3>

        {/* Upload Section - Available to owners and members */}
        {canUpload && (
          <div className="space-y-4">
            {/* Free plan limit banner */}
            {isFreeUser && (
              <div className={`flex items-center justify-between p-3 rounded-lg border ${hasReachedPhotoLimit ? 'bg-amber-50 border-amber-200' : 'bg-sky-50 border-sky-200'}`}>
                <p className={`text-sm font-medium ${hasReachedPhotoLimit ? 'text-amber-700' : 'text-sky-700'}`}>
                  {hasReachedPhotoLimit
                    ? 'You\'ve reached the free plan limit for photos.'
                    : `Free plan: ${photos.length}/${FREE_PHOTO_LIMIT} photos uploaded`}
                </p>
                {hasReachedPhotoLimit && onViewPricing && (
                  <button
                    onClick={onViewPricing}
                    className="px-3 py-1.5 text-xs font-semibold bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors"
                  >
                    Upgrade Plan
                  </button>
                )}
              </div>
            )}
            {/* Drag and Drop Zone */}
            <div
              ref={dropZoneRef}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              className={`relative rounded-lg p-8 border-2 border-dashed transition-all duration-200 ${
                isDragging
                  ? 'border-primary-500 bg-primary-50 scale-[1.02]'
                  : 'border-neutral-300 bg-gradient-to-br from-primary-50 to-secondary-50 hover:border-primary-400'
              }`}
            >
              <div className="flex flex-col items-center gap-4">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center transition-all duration-200 ${
                  isDragging ? 'bg-primary-500 scale-110' : 'bg-gradient-primary'
                }`}>
                  <FaCloudUploadAlt className="text-3xl text-white" />
                </div>
                <div className="text-center">
                  <p className="text-lg font-semibold text-neutral-700">
                    {isDragging ? 'Drop photos here!' : 'Drag & drop photos here'}
                  </p>
                  <p className="text-neutral-500 mt-1">or</p>
                </div>
                <label
                  htmlFor="photo-upload-input"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-primary text-white rounded-lg font-semibold shadow-travel hover:shadow-elevated hover:scale-105 transition-all duration-200 cursor-pointer"
                >
                  <FaUpload />
                  Browse Files
                  <input
                    id="photo-upload-input"
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                    onChange={handleFileSelect}
                    multiple
                    className="hidden"
                  />
                </label>
                <p className="text-sm text-neutral-500">
                  Supports: JPG, PNG, GIF, WebP (max 10MB each)
                </p>
              </div>
            </div>

            {/* Selected Files Preview */}
            {filePreviews.length > 0 && (
              <div className="bg-white rounded-lg border border-neutral-200 p-4 space-y-4 animate-slide-down">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold text-neutral-700">
                    {filePreviews.length} photo{filePreviews.length !== 1 ? 's' : ''} selected
                  </h4>
                  <button
                    onClick={clearAllFiles}
                    className="text-sm text-red-600 hover:text-red-700 font-medium"
                  >
                    Clear All
                  </button>
                </div>

                {/* Preview Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                  {filePreviews.map((preview, index) => (
                    <div key={index} className="relative group">
                      <div className="aspect-square rounded-lg overflow-hidden bg-neutral-100 border border-neutral-200">
                        <img
                          src={preview.url}
                          alt={preview.name}
                          className="w-full h-full object-cover"
                        />
                        {/* Upload Status Overlay */}
                        {uploadProgress[index] && (
                          <div className={`absolute inset-0 flex items-center justify-center ${
                            uploadProgress[index] === 'uploading' ? 'bg-black/50' :
                            uploadProgress[index] === 'success' ? 'bg-green-500/70' :
                            'bg-red-500/70'
                          }`}>
                            {uploadProgress[index] === 'uploading' && (
                              <div className="w-8 h-8 border-3 border-white border-t-transparent rounded-full animate-spin"></div>
                            )}
                            {uploadProgress[index] === 'success' && (
                              <FaCheckCircle className="text-3xl text-white" />
                            )}
                            {uploadProgress[index] === 'error' && (
                              <FaTimes className="text-3xl text-white" />
                            )}
                          </div>
                        )}
                      </div>
                      {/* Remove Button */}
                      {!uploading && (
                        <button
                          onClick={() => removeSelectedFile(index)}
                          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-md hover:bg-red-600"
                        >
                          <FaTimes className="text-xs" />
                        </button>
                      )}
                      {/* Filename */}
                      <p className="mt-1 text-xs text-neutral-600 truncate" title={preview.name}>
                        {preview.name}
                      </p>
                    </div>
                  ))}
                </div>

                {/* Upload Button */}
                <button
                  onClick={handleUpload}
                  disabled={uploading}
                  className="w-full px-6 py-3 bg-gradient-secondary text-white rounded-lg font-semibold shadow-secondary hover:shadow-elevated hover:scale-[1.02] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                  {uploading ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Uploading {Object.keys(uploadProgress).length} of {filePreviews.length}...
                    </span>
                  ) : (
                    `Upload ${filePreviews.length} Photo${filePreviews.length !== 1 ? 's' : ''}`
                  )}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Photos Grid or Empty State */}
        {photos.length === 0 ? (
          <div className="text-center py-12 bg-neutral-50 rounded-lg border-2 border-dashed border-neutral-300">
            <div className="w-20 h-20 bg-neutral-200 rounded-full flex items-center justify-center mx-auto mb-4">
              <FaImage className="text-4xl text-neutral-400" />
            </div>
            <p className="text-neutral-600 font-medium text-lg">
              {canUpload ? 'No photos yet. Upload some memories!' : 'No photos available.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {photos.map((photo, index) => {
              const photoIndex = photos.findIndex(p => p.id === photo.id && photoUrls[p.id]);
              return (
                <div
                  key={photo.id}
                  className="bg-white rounded-xl shadow-medium overflow-hidden hover:shadow-elevated transition-all duration-200 group"
                >
                  {/* Image Container */}
                  <div className="relative aspect-[4/3] bg-neutral-100">
                    {photoUrls[photo.id] ? (
                      <>
                        <img
                          src={photoUrls[photo.id]}
                          alt={photo.filename}
                          className="w-full h-full object-cover cursor-pointer"
                          onClick={() => handleImageClick(photoIndex)}
                        />
                        {/* Zoom Overlay on Hover */}
                        <div
                          className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center cursor-pointer"
                          onClick={() => handleImageClick(photoIndex)}
                        >
                          <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center">
                            <FaSearchPlus className="text-2xl text-primary-500" />
                          </div>
                        </div>
                      </>
                    ) : failedPhotos.has(photo.id) ? (
                      <div className="w-full h-full flex items-center justify-center bg-neutral-50">
                        <div className="flex flex-col items-center gap-2">
                          <FaImage className="text-2xl text-neutral-300" />
                          <p className="text-sm text-neutral-400">Failed to load</p>
                          <button
                            onClick={(e) => { e.stopPropagation(); fetchPhotos(); }}
                            className="text-xs text-primary-500 hover:text-primary-600 font-medium"
                          >Retry</button>
                        </div>
                      </div>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <div className="flex flex-col items-center gap-2">
                          <div className="w-8 h-8 border-3 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
                          <p className="text-sm text-neutral-500">Loading...</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Photo Details */}
                  <div className="p-4 space-y-3">
                    {editingPhotoId === photo.id ? (
                      <div className="space-y-3 animate-slide-down">
                        <textarea
                          value={editComment}
                          onChange={(e) => setEditComment(e.target.value)}
                          placeholder="Add a comment..."
                          rows="3"
                          className="w-full px-3 py-2 rounded-lg border-2 border-neutral-300 focus:border-secondary-500 focus:ring-4 focus:ring-secondary-100 outline-none transition-all resize-none"
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleSaveComment(photo.id)}
                            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 transition-colors"
                          >
                            <FaSave /> Save
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-neutral-200 text-neutral-700 rounded-lg font-medium hover:bg-neutral-300 transition-colors"
                          >
                            <FaTimes /> Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <p className="text-neutral-700 text-sm min-h-[3rem] line-clamp-3">
                          {photo.comment || <span className="italic text-neutral-400">No comment</span>}
                        </p>
                        {isOwner && (
                          <div className="flex gap-2 pt-2 border-t border-neutral-100">
                            <button
                              onClick={() => handleEditComment(photo)}
                              className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-primary-50 text-primary-600 rounded-lg font-medium hover:bg-primary-100 transition-colors text-sm"
                            >
                              <FaEdit /> Edit
                            </button>
                            <button
                              onClick={() => handleDeletePhoto(photo.id)}
                              className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-red-50 text-red-600 rounded-lg font-medium hover:bg-red-100 transition-colors text-sm"
                            >
                              <FaTrash /> Delete
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Image Lightbox */}
      <ImageLightbox
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        images={lightboxImages}
        currentIndex={lightboxIndex}
      />

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ isOpen: false, photoId: null })}
        onConfirm={confirmDeletePhoto}
        title="Delete Photo"
        message="Are you sure you want to delete this photo? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </>
  );
};

export default PhotoGallery;
